import { useEffect, useRef } from 'react';
import { AttributeBadge } from './AttributeBadge.jsx';
import { OptionGroup } from './OptionGroup.jsx';
import { formatPrice } from '../utils/formatPrice.js';
import { hasPricedPortions } from '../utils/hasPricedPortions.js';

export function ItemDetailSheet({ item, currency, itemDisplay, onClose }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
      if (event.key === 'Tab') {
        const dialog = closeButtonRef.current?.closest('[role="dialog"]');
        const focusable = dialog?.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof window.HTMLElement && previouslyFocused.isConnected) previouslyFocused.focus();
    };
  }, [onClose]);

  if (!item) return null;

  const formattedPrice = itemDisplay.showPrice ? formatPrice(item.price, currency) : null;
  const priceLabel = formattedPrice
    ? `${hasPricedPortions(item) ? 'From ' : ''}${formattedPrice}`
    : null;
  const attributes = itemDisplay.showAttributes ? item.attributes ?? [] : [];
  const options = item.options ?? [];
  const hasImage = itemDisplay.showImages && Boolean(item.imageUrl);

  return (
    <>
      {/* Mouse/touch-only affordance — keyboard and screen-reader users
          close via the labeled button below or Escape (handled above). */}
      <div className="rm-sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="rm-sheet" role="dialog" aria-modal="true" aria-labelledby="rm-sheet-item-name">
        <button
          ref={closeButtonRef}
          type="button"
          className={`rm-sheet__close${hasImage ? ' rm-sheet__close--overlay' : ''}`}
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        {hasImage && (
          <img
            className="rm-sheet__image"
            src={item.imageUrl}
            alt=""
            role="presentation"
            decoding="async"
            onError={(event) => {
              event.currentTarget.hidden = true;
            }}
          />
        )}

        <div className="rm-sheet__content">
          <div className="rm-sheet__title-row">
            <h2 id="rm-sheet-item-name" className="rm-sheet__name">
              {item.name}
            </h2>
            {item.isFeatured && <span className="rm-badge rm-badge--featured">★ Popular</span>}
          </div>
          {priceLabel && <div className="rm-sheet__price">{priceLabel}</div>}
          {itemDisplay.showDescription && item.description && <p className="rm-sheet__description">{item.description}</p>}

          {attributes.length > 0 && (
            <>
              <div className="rm-sheet__section-title">Details</div>
              <div className="rm-item-card__badges">
                {attributes.map((attr, i) => (
                  <AttributeBadge key={i} label={attr.label} value={attr.value} />
                ))}
              </div>
            </>
          )}

          {options.map((option, i) => (
            <OptionGroup key={i} option={option} currency={currency} itemPrice={item.price} />
          ))}
        </div>
      </div>
    </>
  );
}
